import { Box, Icon } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "../../../../app/hooks";
import { Talent } from "../../../talents/talentAPI";
import { getTalentProfilePictureDownloadUrl } from "../../../talents/talentsSlice";
import SkeletonWrapper from "../../../../common/components/functional/skeletonWrapper/SkeletonWrapper";
import { isFulfilled } from "@reduxjs/toolkit";
import PersonIcon from "@mui/icons-material/Person";

export enum AvatarShape {
  CIRCULAR = "circular",
  ROUNDED = "rounded",
}

type Props = {
  talent: Talent;
  avatarShape?: AvatarShape;
  size?: number;
};

const TalentProfilePicture = ({
  talent,
  avatarShape = AvatarShape.CIRCULAR,
  size = 48,
}: Props) => {
  const { t } = useTranslation("talentProfile");
  const dispatch = useAppDispatch();

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const profilePicture = talent.account?.profilePicture;
  const showPlaceholder = !profilePicture || hasError;
  const borderRadius = avatarShape === AvatarShape.CIRCULAR ? "100%" : "12px";

  const preloadImage = (url: string, callback: () => void) => {
    let objImagePreloader = new Image();
    objImagePreloader.src = url;
    if (objImagePreloader.complete) {
      setIsLoading(false);
    } else {
      objImagePreloader.onload = () => {
        callback();
        setIsLoading(false);
        objImagePreloader.onload = () => {};
      };
      objImagePreloader.onerror = () => {
        setHasError(true);
        setIsLoading(false);
      };
    }
  };

  const fetchProfilePicture = useCallback(() => {
    if (!profilePicture) return;
    setIsLoading(true);
    setHasError(false);

    dispatch(
      getTalentProfilePictureDownloadUrl({
        pathspec: profilePicture,
        talentId: talent.id,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        preloadImage(action.payload.url, () => {
          setImageUrl(action.payload.url);
        });
      } else {
        setHasError(true);
        setIsLoading(false);
      }
    });
  }, [dispatch, profilePicture, talent.id]);

  useEffect(() => {
    fetchProfilePicture();

    return () => {
      setImageUrl(null);
      setIsLoading(false);
      setHasError(false);
    };
  }, [fetchProfilePicture]);

  return (
    <Box minWidth={size} maxWidth={size} maxHeight={size}>
      {!showPlaceholder ? (
        <SkeletonWrapper
          animation="wave"
          loading={isLoading}
          value={imageUrl}
          sx={{ borderRadius, height: size, width: size }}
        >
          <Box
            component="img"
            src={imageUrl ?? undefined}
            alt={t("profileSummary.profilePictureAlt")}
            onError={() => setHasError(true)}
            sx={{
              width: "100%",
              maxWidth: size,
              maxHeight: size,
              minHeight: size,
              height: "auto",
              objectFit: "cover",
              borderRadius,
            }}
          />
        </SkeletonWrapper>
      ) : (
        <Icon
          sx={{
            width: size,
            height: size,
            color: (theme) => theme.palette.gray.primary,
            backgroundColor: (theme) => theme.palette.gray.light,
            fontSize: size,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius,
          }}
        >
          <PersonIcon fontSize="inherit" />
        </Icon>
      )}
    </Box>
  );
};

export default TalentProfilePicture;
